/**
 * Promotion Slot 공통 컴포넌트.
 *
 * 영구지침: docs/develop/promotion/promotion-slot-spec.md §6.2
 *
 * 사용 예시:
 *   <PromotionSlot slotKey="main_hero" />
 *   <PromotionSlot slotKey="facility_detail_side" fallback={<DefaultBanner />} />
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  usePromotionSlot,
  trackImpression,
  getClickUrl,
  toPromotionImageUrl,
  type PromotionPublic,
} from '@/features/promotion/api'
import { getAdfitUnit } from '@/features/promotion/adfitSlots'
import AdfitBanner from '@/components/common/AdfitBanner'

interface PromotionSlotProps {
  /** 슬롯 키 (예: 'main_hero'). 영구지침 §3 참조. */
  slotKey: string
  /** 시도명 (REGION 타겟팅용, 선택). */
  region?: string
  /** 최대 노출 개수 (기본 1). */
  limit?: number
  /**
   * 레이아웃 (영구지침 §6.6).
   * - 'single' (기본): 세로 나열, limit=1에 적합
   * - 'slider': 가로 스크롤 슬라이더 + snap, 각 카드 min-width 280px
   * - 'grid': 2열 정사각형 그리드 (사이드바 광고 영역용, limit=4 권장)
   */
  layout?: 'single' | 'slider' | 'grid'
  /** 컨테이너 클래스. */
  className?: string
  /** 송출할 광고가 없을 때 렌더할 fallback (선택). */
  fallback?: ReactNode
}

export default function PromotionSlot({
  slotKey,
  region,
  limit = 1,
  layout = 'single',
  className = '',
  fallback = null,
}: PromotionSlotProps) {
  const { data, isLoading } = usePromotionSlot(slotKey, region, limit)

  if (isLoading) {
    // SSR/초기 로딩: 자리만 잡고 출력 안 함 (CLS 방지를 위해 호출 측에서 minHeight 권장)
    return null
  }

  if (!data || data.length === 0) {
    // 하우스(등록) 배너가 없으면 애드핏 fallback(슬롯에 매핑된 광고단위가 있을 때).
    // 매핑이 없으면 기존 fallback prop(하우스 CTA/광고모집 카드 등)을 렌더.
    const adfit = getAdfitUnit(slotKey)
    if (adfit) {
      // 모바일 크기가 지정된(1곳 렌더) 슬롯은 PC/모바일 각각 다른 광고를 CSS로 토글.
      // PC용 728x90 등이 모바일 폭을 넘겨 가로 스크롤을 만드는 것을 방지.
      if (adfit.mobile) {
        return (
          <div className={className}>
            <div className="hidden justify-center lg:flex">
              <AdfitBanner adUnit={adfit.adUnit} width={adfit.width} height={adfit.height} />
            </div>
            <div className="flex justify-center lg:hidden">
              <AdfitBanner adUnit={adfit.mobile.adUnit} width={adfit.mobile.width} height={adfit.mobile.height} />
            </div>
          </div>
        )
      }
      return (
        <div className={`flex justify-center ${className}`}>
          <AdfitBanner adUnit={adfit.adUnit} width={adfit.width} height={adfit.height} />
        </div>
      )
    }
    return <>{fallback}</>
  }

  if (layout === 'slider') {
    return <SliderLayout items={data} className={className} />
  }

  if (layout === 'grid') {
    return (
      <div className={`grid grid-cols-2 gap-2 ${className}`}>
        {data.map((promo) => (
          <PromotionItem key={promo.id} promo={promo} square />
        ))}
      </div>
    )
  }

  return (
    <div className={className}>
      {data.map((promo) => (
        <PromotionItem key={promo.id} promo={promo} />
      ))}
    </div>
  )
}

/* ---------------------------------------------------------------------------
 * 슬라이더 레이아웃 — 스크롤바 숨김 + hover 화살표 + 자동 회전
 * ------------------------------------------------------------------------- */

const AUTO_SCROLL_INTERVAL_MS = 4000

function SliderLayout({
  items,
  className = '',
}: {
  items: PromotionPublic[]
  className?: string
}) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [hovered, setHovered] = useState(false)
  const [overflow, setOverflow] = useState(false)

  // overflow 감지 (마운트 / 리사이즈 / 데이터 변경 시)
  useEffect(() => {
    const check = () => {
      const el = trackRef.current
      if (!el) return
      setOverflow(el.scrollWidth > el.clientWidth + 4)
    }
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [items.length])

  // 자동 회전: hover 중이 아니고 스크롤 가능한 영역이 있을 때 4초 간격으로 다음 카드로
  useEffect(() => {
    if (hovered || items.length <= 1) return
    const timer = setInterval(() => {
      const el = trackRef.current
      if (!el) return
      const maxScroll = el.scrollWidth - el.clientWidth
      // 스크롤할 여백이 없는 화면(모든 카드가 다 보이는 경우)이면 회전 의미 없음
      if (maxScroll <= 8) return
      const cardWidth = el.firstElementChild
        ? (el.firstElementChild as HTMLElement).offsetWidth + 12 // gap-3
        : 300
      // 이미 끝에 닿았으면 처음으로, 아니면 한 카드만큼 전진(끝까지로 클램프)
      const atEnd = el.scrollLeft >= maxScroll - 8
      const next = atEnd ? 0 : Math.min(el.scrollLeft + cardWidth, maxScroll)
      el.scrollTo({ left: next, behavior: 'smooth' })
    }, AUTO_SCROLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [hovered, items.length])

  const scrollByCard = (dir: 'left' | 'right') => {
    const el = trackRef.current
    if (!el) return
    const cardWidth = el.firstElementChild
      ? (el.firstElementChild as HTMLElement).offsetWidth + 12
      : 300
    el.scrollBy({
      left: dir === 'right' ? cardWidth : -cardWidth,
      behavior: 'smooth',
    })
  }

  return (
    <div
      className={`group relative ${className}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        ref={trackRef}
        className="scrollbar-hide flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2"
      >
        {items.map((promo) => (
          <div
            key={promo.id}
            className="min-w-[280px] max-w-[320px] flex-shrink-0 snap-start"
          >
            <PromotionItem promo={promo} />
          </div>
        ))}
      </div>

      {/* overflow 발생 시에만, hover 시에만 좌우 버튼 노출 */}
      {overflow && (
        <>
          <button
            type="button"
            onClick={() => scrollByCard('left')}
            aria-label="이전"
            className="absolute left-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white text-cb-gray-700 shadow-lg opacity-0 transition-opacity duration-200 hover:bg-cb-gray-50 group-hover:opacity-95"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={() => scrollByCard('right')}
            aria-label="다음"
            className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white text-cb-gray-700 shadow-lg opacity-0 transition-opacity duration-200 hover:bg-cb-gray-50 group-hover:opacity-95"
          >
            <ChevronRight size={18} />
          </button>
        </>
      )}
    </div>
  )
}

/* ---------------------------------------------------------------------------
 * 개별 항목
 * ------------------------------------------------------------------------- */

function PromotionItem({
  promo,
  square = false,
}: {
  promo: PromotionPublic
  /** 정사각 비율 강제 (그리드 레이아웃용). */
  square?: boolean
}) {
  const ref = useRef<HTMLAnchorElement>(null)
  const trackedRef = useRef(false)

  // IntersectionObserver: 50% 이상 노출 시 1회 impression 트래킹 (브라우저 전용)
  useEffect(() => {
    if (typeof window === 'undefined' || !ref.current || trackedRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !trackedRef.current) {
            trackedRef.current = true
            trackImpression(promo.id)
            observer.disconnect()
            break
          }
        }
      },
      { threshold: 0.5 }
    )

    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [promo.id])

  // 데스크톱(lg ≥1024px): imageUrl, 모바일: imageUrlMobile (있으면) — <picture>+srcSet 으로 브라우저 자동 결정
  const imageSrc = toPromotionImageUrl(promo.imageUrl)
  const mobileImageSrc = promo.imageUrlMobile
    ? toPromotionImageUrl(promo.imageUrlMobile)
    : null
  const isAd = promo.badgeType === 'AD' || promo.badgeType === 'SPONSORED'
  const linkProps =
    promo.linkTarget === 'BLANK'
      ? { target: '_blank', rel: 'noopener noreferrer sponsored' }
      : {}

  // 클릭은 항상 백엔드 트래킹 엔드포인트를 거친다 (등록된 link_url로 302)
  const href = promo.linkUrl ? getClickUrl(promo.id) : '#'

  return (
    <a
      ref={ref}
      href={href}
      {...linkProps}
      className={`group relative block overflow-hidden rounded-2xl bg-cb-gray-50 ${
        square ? 'aspect-square' : ''
      }`}
      aria-label={promo.title}
    >
      {imageSrc ? (
        <picture>
          {mobileImageSrc && (
            <source media="(max-width: 1023px)" srcSet={mobileImageSrc} />
          )}
          <img
            src={imageSrc}
            alt={promo.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
          />
        </picture>
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-cb-primary-light to-white p-4">
          <div className="text-center">
            <h3 className={`font-bold text-cb-primary ${square ? 'text-sm' : 'text-lg'}`}>
              {promo.title}
            </h3>
            {promo.description && !square && (
              <p className="mt-2 text-sm text-cb-gray-600">{promo.description}</p>
            )}
          </div>
        </div>
      )}

      {/* 광고 식별 라벨 (AD/SPONSORED만) */}
      {isAd && (
        <span className="absolute right-2 top-2 rounded-md bg-black/60 px-2 py-0.5 text-[10px] font-bold text-white backdrop-blur-sm">
          광고
        </span>
      )}
    </a>
  )
}
